import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  PhoneCall,
  Send,
  Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import TID from "@/lib/testIds";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Penetration Testing",
  "Web Application Security",
  "API Security Testing",
  "Network Security Assessment",
  "Vulnerability Assessment",
  "Security Consultation",
  "Social Media Marketing",
  "Meta Ads Management",
  "Google Ads",
  "SEO",
  "Content Marketing",
  "Brand Growth Strategy",
  "Website Development",
];

const BUDGETS = [
  "Under $1,000",
  "$1,000 – $5,000",
  "$5,000 – $15,000",
  "$15,000 – $50,000",
  "$50,000+",
  "Not sure yet",
];

const init = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: "",
  budget: "",
  message: "",
  website: "", // honeypot
};

export const Contact = () => {
  const [form, setForm] = useState(init);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [serverMsg, setServerMsg] = useState("");

  const set = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v?.target?.value ?? "" }));

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Please enter your name";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone || form.phone.trim().length < 5) e.phone = "Enter a valid phone";
    if (!form.service) e.service = "Choose a service";
    if (!form.budget) e.budget = "Choose a budget";
    if (!form.message || form.message.trim().length < 5)
      e.message = "Tell us a bit more (5+ chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    setServerMsg("");
    try {
      const { data } = await axios.post(`${API_BASE}/contact`, form);
      if (data?.ok) {
        setStatus("success");
        setForm(init);
        setErrors({});
      } else {
        setStatus("error");
        setServerMsg("Something went wrong. Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setServerMsg(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Network error. Please try again or message us on WhatsApp.",
      );
    }
  };

  return (
    <section
      id="contact"
      data-testid={TID.contactSection}
      className="relative py-24 md:py-32"
    >
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-10 left-1/4 w-[480px] h-[480px] rounded-full bg-brand-blue/15 blur-3xl animate-blob-float" />
        <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full bg-brand-purple/15 blur-3xl animate-blob-float [animation-delay:-8s]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-10 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="lg:col-span-2"
        >
          <p className="text-sm font-semibold text-brand-blue uppercase tracking-[0.18em]">Get in touch</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            Let&apos;s build something <span className="text-gradient-brand">secure & remarkable</span>
          </h2>
          <p className="mt-5 text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed">
            Share a few details and we&apos;ll get back within a few business hours
            with a clear next-step plan.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href={`mailto:${process.env.REACT_APP_COMPANY_EMAIL || "contact@naksec.com"}`}
              className="flex items-center gap-3 p-4 rounded-2xl glass shadow-elevated hover:translate-y-[-2px] transition-transform"
            >
              <span className="w-10 h-10 rounded-xl bg-brand-blue/10 text-brand-blue inline-flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {process.env.REACT_APP_COMPANY_EMAIL || "contact@naksec.com"}
                </p>
              </div>
            </a>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={TID.contactWhatsAppCta}
              className="flex items-center gap-3 p-4 rounded-2xl glass shadow-elevated hover:translate-y-[-2px] transition-transform"
            >
              <span className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 inline-flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">WhatsApp</p>
                <p className="font-semibold text-slate-900 dark:text-white">Chat with us instantly</p>
              </div>
            </a>
            <div className="flex items-center gap-3 p-4 rounded-2xl glass shadow-elevated">
              <span className="w-10 h-10 rounded-xl bg-brand-purple/10 text-brand-purple inline-flex items-center justify-center">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Working hours</p>
                <p className="font-semibold text-slate-900 dark:text-white">Mon–Sat · 10:00 – 19:00 IST</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onSubmit={onSubmit}
          className="lg:col-span-3 rounded-3xl p-6 md:p-9 bg-white dark:bg-[#0F172A]/80 border border-slate-200 dark:border-white/10 shadow-elevated"
          noValidate
        >
          {/* Honeypot */}
          <div className="hidden" aria-hidden>
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              data-testid={TID.contactHoneypot}
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={set("website")}
            />
          </div>

          {status === "success" ? (
            <motion.div
              data-testid={TID.contactSuccess}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-12 text-center"
            >
              <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/10 text-emerald-600 inline-flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold text-slate-900 dark:text-white">
                Thanks — your message is on its way!
              </h3>
              <p className="mt-2 text-slate-600 dark:text-slate-300">
                We&apos;ll reach out within a few business hours.
              </p>
              <div className="mt-6 flex justify-center gap-3 flex-wrap">
                <a
                  href={buildWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 items-center px-5 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Continue on WhatsApp
                </a>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="h-11 px-5 rounded-full border border-slate-200 dark:border-white/10 font-semibold"
                >
                  Send another
                </button>
              </div>
            </motion.div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" error={errors.name}>
                  <Input
                    data-testid={TID.contactName}
                    placeholder="Jane Cooper"
                    value={form.name}
                    onChange={set("name")}
                    className="h-12 rounded-xl"
                  />
                </Field>
                <Field label="Company" optional>
                  <Input
                    data-testid={TID.contactCompany}
                    placeholder="Acme Inc."
                    value={form.company}
                    onChange={set("company")}
                    className="h-12 rounded-xl"
                  />
                </Field>
                <Field label="Email" error={errors.email}>
                  <Input
                    data-testid={TID.contactEmail}
                    type="email"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={set("email")}
                    className="h-12 rounded-xl"
                  />
                </Field>
                <Field label="Phone" error={errors.phone}>
                  <Input
                    data-testid={TID.contactPhone}
                    placeholder="+91 98765 43210"
                    value={form.phone}
                    onChange={set("phone")}
                    className="h-12 rounded-xl"
                  />
                </Field>
                <Field label="Service required" error={errors.service}>
                  <Select value={form.service} onValueChange={set("service")}>
                    <SelectTrigger
                      data-testid={TID.contactService}
                      className="h-12 rounded-xl"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Budget" error={errors.budget}>
                  <Select value={form.budget} onValueChange={set("budget")}>
                    <SelectTrigger
                      data-testid={TID.contactBudget}
                      className="h-12 rounded-xl"
                    >
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGETS.map((b) => (
                        <SelectItem key={b} value={b}>
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Project details" error={errors.message}>
                  <Textarea
                    data-testid={TID.contactMessage}
                    placeholder="Tell us about your goals, timeline, and any context..."
                    value={form.message}
                    onChange={set("message")}
                    className="min-h-[140px] rounded-xl"
                  />
                </Field>
              </div>

              {status === "error" && serverMsg && (
                <p className="mt-4 text-sm text-rose-600">{String(serverMsg)}</p>
              )}

              <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                <p className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  We respond within a few business hours.
                </p>
                <Button
                  type="submit"
                  data-testid={TID.contactSubmit}
                  disabled={status === "loading"}
                  className="h-12 px-7 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple text-white font-semibold shadow-elevated hover:opacity-95 btn-ripple"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, error, optional, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5 inline-flex items-center gap-1">
      {label}
      {optional && (
        <span className="text-[10px] uppercase tracking-wider text-slate-400">optional</span>
      )}
    </Label>
    {children}
    {error && <p className="mt-1.5 text-xs text-rose-600">{error}</p>}
  </div>
);

export default Contact;
